import { PageLayout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Eye, Warning, UserCircle, Lock, Phone } from '@phosphor-icons/react';

const AntiStalkingPolicy = () => {
  return (
    <PageLayout>
      <div className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
              <ShieldCheck weight="light" className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">Anti-Stalking & Privacy Policy</h1>
            <p className="text-muted-foreground">
              Your safety and privacy are our top priorities
            </p>
            <p className="text-sm text-muted-foreground mt-2">Last Updated: December 2025</p>
          </div>

          {/* Content */}
          <div className="paper-card rounded-lg p-8 md:p-12 space-y-8">
            
            {/* Introduction */}
            <section>
              <p className="text-muted-foreground leading-relaxed">
                Link, Laugh & Love is committed to providing a safe and secure matchmaking environment. 
                We take stalking, harassment, and privacy violations extremely seriously. This policy 
                outlines our commitment to protecting our clients and the measures we take to ensure 
                your safety throughout the matchmaking process.
              </p>
            </section>

            {/* Zero Tolerance */}
            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <Warning weight="fill" className="w-6 h-6 text-red-500" />
                Zero Tolerance Policy
              </h2>
              <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                <p className="text-gray-700 leading-relaxed">
                  Link, Laugh & Love maintains a <strong>zero tolerance policy</strong> for stalking, 
                  harassment, intimidation, or any behavior that threatens the safety or well-being of our clients.
                </p>
              </div>
            </section>

            {/* Prohibited Behaviors */}
            <section>
              <h2 className="font-serif text-2xl mb-4">Prohibited Behaviors</h2>
              <p className="text-muted-foreground mb-4">The following behaviors are strictly prohibited:</p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">•</span>
                  Following or surveilling another client without consent
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">•</span>
                  Unwanted contact after being asked to stop
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">•</span>
                  Attempting to obtain personal information through deceptive means
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">•</span>
                  Sharing another client's personal information without consent
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">•</span>
                  Threatening, intimidating, or harassing behavior
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">•</span>
                  Showing up uninvited at a client's home, workplace, or other locations
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">•</span>
                  Creating fake profiles or misrepresenting identity
                </li>
              </ul>
            </section>

            {/* Privacy Protection */}
            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <Lock weight="fill" className="w-6 h-6 text-primary" />
                Privacy Protection Measures
              </h2>
              <p className="text-muted-foreground mb-4">We protect your privacy through:</p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <ShieldCheck weight="fill" className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Confidential handling of all personal information
                </li>
                <li className="flex items-start gap-3">
                  <ShieldCheck weight="fill" className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Controlled information sharing - only necessary details are shared with potential matches
                </li>
                <li className="flex items-start gap-3">
                  <ShieldCheck weight="fill" className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  No sharing of home addresses, workplace locations, or sensitive personal data
                </li>
                <li className="flex items-start gap-3">
                  <ShieldCheck weight="fill" className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Secure communication channels for all interactions
                </li>
                <li className="flex items-start gap-3">
                  <ShieldCheck weight="fill" className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Right to remain anonymous until you choose to share information
                </li>
              </ul>
            </section>

            {/* Reporting */}
            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <Eye weight="fill" className="w-6 h-6 text-primary" />
                Reporting Concerns
              </h2>
              <p className="text-muted-foreground mb-4">
                If you experience or witness any concerning behavior, please report it immediately:
              </p>
              <div className="bg-pink-50 rounded-lg p-6 space-y-3">
                <p className="flex items-center gap-2">
                  <Phone weight="fill" className="w-5 h-5 text-primary" />
                  <strong>Phone:</strong> (810) 799-1060
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-primary">@</span>
                  <strong>Email:</strong> LinkLaughLoveMatch@gmail.com
                </p>
              </div>
              <p className="text-muted-foreground mt-4 text-sm">
                All reports are taken seriously and investigated promptly. Your identity will be kept 
                confidential throughout the investigation process.
              </p>
            </section>

            {/* Consequences */}
            <section>
              <h2 className="font-serif text-2xl mb-4">Consequences of Violations</h2>
              <p className="text-muted-foreground mb-4">
                Violations of this policy may result in:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="text-primary">•</span>
                  Immediate termination of services without refund
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">•</span>
                  Permanent ban from Link, Laugh & Love services
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">•</span>
                  Reporting to appropriate law enforcement authorities
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">•</span>
                  Legal action as warranted
                </li>
              </ul>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <UserCircle weight="fill" className="w-6 h-6 text-primary" />
                Your Rights
              </h2>
              <p className="text-muted-foreground mb-4">As a client, you have the right to:</p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <ShieldCheck weight="fill" className="w-4 h-4 text-green-500" />
                  Feel safe throughout the matchmaking process
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck weight="fill" className="w-4 h-4 text-green-500" />
                  Control what personal information is shared
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck weight="fill" className="w-4 h-4 text-green-500" />
                  End communication with any match at any time
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck weight="fill" className="w-4 h-4 text-green-500" />
                  Report concerns without fear of retaliation
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck weight="fill" className="w-4 h-4 text-green-500" />
                  Request removal of your information from our system
                </li>
              </ul>
            </section>

            {/* Commitment */}
            <section className="border-t border-pink-200 pt-8">
              <div className="bg-gradient-to-r from-primary/10 to-pink-50 rounded-lg p-6 text-center">
                <h3 className="font-serif text-xl mb-3">Our Commitment to You</h3>
                <p className="text-muted-foreground">
                  Link, Laugh & Love is dedicated to creating a safe, respectful, and positive 
                  matchmaking experience. Your trust is important to us, and we will always 
                  prioritize your safety and privacy.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="text-center">
              <p className="text-muted-foreground mb-4">
                Questions about this policy? Contact us:
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <a href="mailto:LinkLaughLoveMatch@gmail.com" className="text-primary hover:underline">
                  LinkLaughLoveMatch@gmail.com
                </a>
                <a href="tel:8107991060" className="text-primary hover:underline">
                  (810) 799-1060
                </a>
              </div>
            </section>
          </div>

          <div className="text-center mt-8">
            <Link to="/" className="inline-flex items-center gap-2 text-primary hover:underline">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default AntiStalkingPolicy;
